import type { DeleteDataImportByIdMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response ({ affected_rows }) to the GraphQL mutation
 * response shape so consumers branching on the result do not need to know which
 * path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): DeleteDataImportByIdMutation => ({
  delete_data_import: {
    __typename: 'data_import_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useDeleteDataImportByIdTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dataImport.deleteDataImportById.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImports.queryKey(),
        });

        // The detail query is keyed per-id (useGetDataImportById →
        // dataImportById.queryOptions({ id })); invalidate the whole family so
        // the deleted record is not left cached on the update page / back-nav.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImportById.queryKey(),
        });
      },
    }),
  });

  return {
    deleteDataImportById: async (
      id: string
    ): Promise<DeleteDataImportByIdMutation> => {
      const result = await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
